import { Expand, getOneUsingOData } from "@ccq/ts-lib";
import { TrustAsHtmlHelper } from "@js/helpers/TrustAsHtmlHelper";
import {
  ApplicationUser,
  ApplicationUserCrudClient,
  BrickflowEventLogClient,
  BridgingDeal,
  CaseAccessLevelEnum,
  ClaimEnumsInClaimGroup,
  Client,
  CommercialDeal,
  DealClient,
  DevelopmentFinanceDeal,
  EventLogEnum,
  IncorrectDataTypeEnum,
  LenderClient,
  LenderProductPairDTO,
  MakeReferralMessageRequest,
  Organisation,
  OrganisationClient,
  OrganisationLinkClient,
  SaveAsSearchRequest,
  ShareDealDTO,
  UserClient,
  UserIdEmailPairDTO,
} from "@src/apis/ModularClient";
import { IndexService } from "@js/services/IndexService";
import { RoleService } from "@js/services/RoleService";
import { SharedDataService } from "@js/services/SharedDataService";
import { HasActiveLicense } from "@js/helpers/LicenseStatusHelper";
import { ODataHelper } from "@js/helpers/ODataHelper";
import DealHelper from "@js/helpers/DealHelper";

export abstract class BaseLenderResultsScreenController<
  T extends BridgingDeal | CommercialDeal | DevelopmentFinanceDeal,
> {
  loanCriteria: T;
  brokerageOrg: Organisation;
  orgCode: string;
  showRegisterModal: boolean = false;
  showLoginSection: boolean = false;
  enterpriseClient: ApplicationUser = {
    id: 0,
    agreedToTermsAndPP: true,
  } as ApplicationUser;
  isLoggedInUser: boolean = false;
  existingUsers: UserIdEmailPairDTO[] = [];
  deletedDealLenders: LenderProductPairDTO[];
  searchid: number = null;
  isShortlistingMore: boolean = false;
  userLenderId: number = null;
  isLenderReferringSearch: boolean = false;
  lenderReferralData: MakeReferralMessageRequest = {} as MakeReferralMessageRequest;
  loadingData: boolean = false;
  clientOrganisationPhoneNumber: string;
  isClient: boolean = false;
  isLegacyUser: boolean = false;
  selectedUserName: string;
  selectedBorrowerId: number;
  isIntroducer: boolean = false;
  isIntroduceronly: boolean = false;
  isLender: boolean = false;
  isBroker: boolean = false;
  isAdmin: boolean = false;
  hasLiveSearch: boolean = true;
  hasActiveSubscription: boolean = false;
  isConnectUser: boolean = false;
  selectedUser: Expand<ApplicationUser>;
  shareDealDto: ShareDealDTO = {
    isApplicant: true,
    isPrimary: true,
  } as ShareDealDTO;
  saveAsSearchName: string;
  displayMsg: string = null;
  showMsg: boolean = false;
  isSaveAsClicked: boolean = false;
  showSaveResults: number;
  newSearch: boolean = false;
  existingborrower: boolean;
  showClientDetails: boolean = false;
  selecteduserName: string;
  sharedSearch: boolean = false;
  tempSearchName: string = null;

  showFlaggingOptions: boolean[];
  showFlaggingModal: boolean;
  flaggingDetails: string = "";
  incorrectDataType: IncorrectDataTypeEnum;
  flaggedSuccessMessage: string;
  showFlaggedSuccessMessage: boolean;
  isSortAscending: boolean = true;
  shortlistDisabled: boolean = false;
  searchPanelForm: ng.IFormController;
  showPostcodeErrorMessage: boolean = false;
  noOfShortlistAllowed: number = DealHelper.noOfShortlistAllowed;

  static $inject = [
    "$rootScope",
    "$location",
    "ApplicationUserCrudClient",
    "IndexService",
    "RoleService",
    "LenderClient",
    "UserClient",
    "SharedDataService",
    "OrganisationClient",
    "BrickflowEventLogClient",
    "OrganisationLinkClient",
    "DealClient",
    "ODataHelper",
    "TrustAsHtmlHelper",
  ];
  constructor(
    protected $rootScope: ng.IRootScopeService,
    protected $location: ng.ILocationService,
    protected applicationUserCrudClient: ApplicationUserCrudClient,
    protected indexService: IndexService,
    protected roleService: RoleService,
    protected lenderClient: LenderClient,
    protected userClient: UserClient,
    protected sharedDataService: SharedDataService,
    protected organisationClient: OrganisationClient,
    protected eventLogClient: BrickflowEventLogClient,
    protected organisationLinkClient: OrganisationLinkClient,
    protected dealClient: DealClient,
    protected odataHelper: ODataHelper,
    protected trustAsHtmlHelper: TrustAsHtmlHelper,
  ) {}

  saveSaveAsSearch(attachClient = false) {
    this.loadingData = true;
    let newSearchName = this.isBroker || this.isAdmin ? this.shareDealDto.dealName : this.saveAsSearchName;

    if (newSearchName.length == 0) {
      newSearchName = this.loanCriteria.name + " (Copy)";
    }

    const request: SaveAsSearchRequest = {
      shareDealDto: attachClient ? this.shareDealDto : null,
      productFamily: this.loanCriteria.productFamily,
      searchId: this.loanCriteria.id,
      newSearchName: newSearchName,
    };

    this.dealClient
      .saveSaveAsSearch(request)
      .then((result) => {
        this.displayMsg = `${newSearchName} search has been saved to your dashboard.`;
      })
      .catch((error) => {
        this.displayMsg = "Sorry, something went wrong. Please try again.";
      })
      .finally(() => {
        this.cancelNewSearch();
        this.loadingData = false;
        this.isSaveAsClicked = false;
        this.shareDealDto = {
          isApplicant: true,
          isPrimary: true,
        } as ShareDealDTO;
        this.saveAsSearchName = "";
        this.showSaveResults = null;
        this.showMsg = true;
      });
  }

  /** closes share results modal*/
  cancelNewSearch() {
    this.newSearch = false;
    this.shareDealDto.clientDto = null;
    this.existingborrower = null;
    this.showClientDetails = false;
    this.selecteduserName = null;
    this.isSaveAsClicked = false;
    this.sharedSearch = false;
    this.selectedBorrowerId = null;
    if (this.tempSearchName) {
      this.loanCriteria.name = this.tempSearchName;
      this.tempSearchName = "";
    }
  }

  abstract updateCriteriaAndGetResults(criteriaChanged: boolean, updateLoanLabels?: boolean);
  clearAllFilters(): void {
    this.doClearPropertyFilters();
    this.doClearClientFilters();
    this.doClearFinanceFilters();
    this.updateCriteriaAndGetResults(false);
  }

  clearClientFilters(): void {
    this.doClearClientFilters();
    this.updateCriteriaAndGetResults(false);
  }

  clearPropertyFilters(): void {
    this.doClearPropertyFilters();
    this.updateCriteriaAndGetResults(false);
  }

  clearFinanceFilters(): void {
    this.doClearFinanceFilters();
    this.updateCriteriaAndGetResults(false);
  }
  abstract doClearClientFilters(): void;
  abstract doClearPropertyFilters(): void;
  abstract doClearFinanceFilters(): void;

  protected async initialiseProfileData() {
    this.selectedUser = await getOneUsingOData(this.applicationUserCrudClient, {
      filter: { id: { eq: await this.indexService.currentUserId } },
    });
    this.selectedUserName = this.selectedUser?.userName;
    this.isLegacyUser = this.selectedUser?.isLegacyUser;
    this.isConnectUser = this.selectedUser?.isConnectUser;
    const licenseData = await this.indexService.currentUserLicenseData;
    this.selectedUser.subscriptionStatus = licenseData.subscriptionStatus;
    this.selectedUser.hasActiveSubscription = this.hasActiveSubscription = HasActiveLicense(
      licenseData.subscriptionStatus,
    );

    this.roleService.isClientUser().then((response) => {
      this.isClient = response;

      if (this.clientOrganisationPhoneNumber && !this.isClient) {
        this.clientOrganisationPhoneNumber = null;
      }
    });

    this.roleService.isAdminUser().then((response) => {
      this.isAdmin = response;
      if (this.isAdmin) {
        this.getUsersBelongToBrokerOrAdmin();
      }
    });

    this.roleService.isBroker().then((response) => {
      this.isBroker = response;
      if (this.isBroker) {
        this.getUsersBelongToBrokerOrAdmin();
      }
    });

    this.roleService.isIntroducer().then((response) => {
      this.isIntroducer = response;
    });

    this.roleService.isIntroducerOnly().then((response) => {
      this.isIntroduceronly = response;
    });

    this.roleService.isLenderUser().then((response) => {
      this.isLender = response;
      if (this.isLender) {
        this.hasLiveSearch = false;
        this.lenderClient.getUserLenderDetails().then((lender) => {
          if (lender.hasLiveMarketSearch) {
            this.hasLiveSearch = true;
          }
        });

        //Lender referred search
        if (sessionStorage.getItem("lenderReferralReference")) {
          this.lenderReferralData.dealName = sessionStorage.getItem("lenderReferralReference");
          this.isLenderReferringSearch = true;
        }
        this.userClient.getCurrentUserLender().then((response) => {
          this.userLenderId = response;
        });
      }
    });
    if (this.isShortlistingMore) {
      this.lenderClient
        .getDealDeletedLenderIds(this.searchid)
        .then((response) => {
          this.deletedDealLenders = response;
        })
        .finally(() => {
          this.getSearchCriteriaAndResults();
        });
    } else {
      this.getSearchCriteriaAndResults();
    }
  }

  abstract getSearchCriteriaAndResults(): any;

  getUsersBelongToBrokerOrAdmin() {
    this.loadingData = true;
    this.userClient.getUserSummariesForBrokerOrAdmin().then((response) => {
      this.existingUsers = response;
    });
  }

  async goToNewSearch() {
    this.sharedDataService.cleanLenderReferralSessionStorage();
    // If the current user is a broker and doesn't have a subscription then don't allow them to create a new search
    if (this.isBroker && !this.hasActiveSubscription) {
      return;
    }

    if (!this.loadingData) {
      (this.$rootScope as any).loanCriteria = null;

      this.$location.path("/allloans");
    }
  }

  populateEnterpriseClientValue(eventType) {
    if (this.loanCriteria.dealClients[0] != null) {
      this.enterpriseClient.firstName = this.loanCriteria.dealClients[0].client.firstName;
      this.enterpriseClient.lastName = this.loanCriteria.dealClients[0].client.lastName;
      this.enterpriseClient.email = this.loanCriteria.dealClients[0].client.email;
      this.enterpriseClient.userName = this.loanCriteria.dealClients[0].client.email;
      this.enterpriseClient.oidcId = this.loanCriteria.dealClients[0].client.email;
      this.enterpriseClient.dealClientUniqueRef = this.loanCriteria.dealClients[0].uniqueRef;
      this.enterpriseClient.orgCode = this.orgCode;
      this.enterpriseClient.phoneNumber = this.loanCriteria.dealClients[0].client.phoneNumber;
      if (this.loanCriteria.dealClients[0].client.clientUserId) {
        this.showRegisterModal = true;
        this.showLoginSection = true;
      }

      this.getOrganisationAndBrokerDetails();

      if (this.loanCriteria.organisationLinkId) {
        this.organisationLinkClient
          .getOrganisationLinkLogoByLinkIdAndLogPageLoadEvent(
            Number(this.loanCriteria.organisationLinkId),
            eventType,
            this.loanCriteria.id,
            this.loanCriteria.productType,
            this.loanCriteria.dealClients[0]?.client?.email,
            this.loanCriteria.dealClients[0]?.client?.id,
          )
          .then((response) => {
            if (response) {
              const imgs = document.getElementsByTagName("img");
              imgs[0].src = response;
            }
          });
      } else {
        this.eventLogClient.logPageLoad(
          eventType,
          this.orgCode ?? "",
          this.loanCriteria.dealClients.length > 0 ? this.loanCriteria.dealClients[0].client.email : "",
          "Client",
          this.loanCriteria.organisationLinkId != null ? this.loanCriteria.organisationLinkId : null,
          this.loanCriteria?.productType,
          "",
          this.loanCriteria?.id,
          this.loanCriteria.dealClients.length > 0 ? this.loanCriteria.dealClients[0].client.id : null,
        );
      }
    }
  }

  getOrganisationAndBrokerDetails() {
    this.loadingData = true;
    if (!this.isLoggedInUser && !this.isAdmin && !this.isBroker) {
      this.loadingData = true;
      this.organisationClient
        .getOrganisationAndBrokerByDealId(this.loanCriteria.id)
        .then((response) => {
          if (response && response.organisation != null) {
            this.brokerageOrg = response.organisation;
          }
        })
        .finally(() => {
          this.loadingData = false;
        });
    }
  }

  showShareSearchModal() {
    this.newSearch = true;

    this.shareDealDto = {
      dealId: this.searchid,
      dealName: this.isSaveAsClicked ? "" : this.loanCriteria?.name,
      showLenderNamesAndLogos: this.loanCriteria?.showLenderInfoBrokerOverride,
      emailLinkToClient: false,
      accessLevel: CaseAccessLevelEnum.FullAccess,
      isApplicant: true,
      isPrimary: true,
      clientDto: null,
    };
  }

  viewClientsDashboardForNewSearch(id: number) {
    this.loadingData = true;
    this.shareDealDto.isApplicant = true;
    this.shareDealDto.isPrimary = true;
    //Look up client's account details
    this.odataHelper
      .getOne(this.applicationUserCrudClient, {
        filter: { id: { eq: id } },
        select: {
          id: true,
          email: true,
          firstName: true,
          lastName: true,
          phoneNumber: true,
        },
      })
      .then((user) => {
        const client = {
          firstName: user.firstName,
          email: user.email,
          lastName: user.lastName,
          phoneNumber: user.phoneNumber,
          clientUserId: user.id,
        } as Client;

        this.shareDealDto.clientDto = client;

        this.showClientDetails = true;
        this.loadingData = false;
      });
  }

  trustAsHtml(content) {
    return this.trustAsHtmlHelper.trustAsHtml(content);
  }

  setFlaggedSuccessMessage(message) {
    this.flaggedSuccessMessage = message;
    this.showFlaggedSuccessMessage = this.flaggedSuccessMessage.length > 0 ? true : false;
  }

  closeFlaggingDropdown(row) {
    if (this.showFlaggingOptions) {
      this.showFlaggingOptions[row] = false;
    }
  }

  logEvent(eventName, eventLogType = EventLogEnum.Export) {
    this.eventLogClient.logEvent(eventName, eventLogType, this.loanCriteria.productType, this.loanCriteria.id);
  }

  closeFlaggingModal() {
    this.showFlaggingModal = false;
    this.flaggingDetails = "";
  }

  isButtonDisabled() {
    if (
      (this.searchPanelForm && this.searchPanelForm.$invalid) ||
      this.showPostcodeErrorMessage ||
      this.shortlistDisabled
    ) {
      return true;
    }

    return false;
  }
}
